import styled from '@emotion/styled';
import { Cached as CachedIcon, ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { Alert, Grid, Button as MuiButton, Divider as MuiDivider, Typography } from '@mui/material';
import React, { cloneElement, useState } from 'react';
import {
  BooleanInput,
  Button,
  DateInput,
  Edit,
  FileField,
  FileInput,
  FormTab,
  ImageField,
  ImageInput,
  LoadingIndicator,
  NumberInput,
  PasswordInput,
  ReferenceArrayInput,
  SaveButton,
  SelectArrayInput,
  SimpleShowLayout,
  TabbedForm,
  TextField,
  TextInput,
  ShowButton,
  Toolbar,
  ToolbarProps,
  TopToolbar,
  email,
  maxValue,
  minValue,
  useDataProvider,
  useEditContext,
  useNotify,
  usePermissions,
  useRecordContext,
  useRedirect,
  useRefresh,
} from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Permission, PriceInput, Tooltip } from '../../components';
import { EntityChangeLogTab } from '../change-log/EntityChangeLogTab';
import { MigrateGroupsToTagsDialog } from './MigrateGroupsToTagsDialog';
import { CNPJInput, PhoneInput } from '../../components/Inputs';
import { Confirm } from 'ra-ui-materialui';
import { DEFAULT_API_ADMIN, PERMISSIONS } from '../../constants';
import { __t } from '../../i18n/t';
import { getChainId, httpClientReactAdmin, saveSelectedChain } from '../../lib';
import { APP_CONFIG_TAB_PATHS, appConfigTabUrl } from '../appConfig/tabs';

const containerIdFieldSx = {
  display: 'flex',
  padding: 0,
  alignItems: 'center',
};

const Divider = styled(MuiDivider)({
  width: '100%',
  marginTop: 12,
  marginBottom: 12,
});

// A tela Aplicativo edita a rede selecionada na barra, não a da URL: abrir outra rede
// por aqui troca a seleção antes, como o seletor da barra faz.
const MovedToAppConfigNotice = () => {
  const record = useRecordContext();
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  if (!record) { return null; }

  // A tela Aplicativo pede Configurações; ser admin não basta. Sem ela, o botão
  // trocaria a rede selecionada e cairia numa página que não abre.
  const canOpen = !!permissions?.includes(PERMISSIONS.SETTINGS);
  const isSelected = record.id === getChainId();

  const openAppConfig = () => {
    navigate(appConfigTabUrl(APP_CONFIG_TAB_PATHS.identity));
    if (!isSelected) {
      saveSelectedChain(record);
      window.location.reload();
    }
  };

  return (
    <Alert
      severity="info"
      sx={{ mb: 1 }}
      action={canOpen ? (
        <MuiButton color="inherit" size="small" onClick={openAppConfig}>
          {isSelected ? __t('Abrir em Aplicativo') : __t('Selecionar esta rede e abrir Aplicativo')}
        </MuiButton>
      ) : undefined}
    >
      Identidade visual, lojas e build mínimo, verificação do cadastro, código temporário, “Somente encher tanque” e odômetro agora ficam em Aplicativo.
      {!canOpen && (
        <Typography variant="inherit" component="span" sx={{ display: 'block', mt: 0.5 }}>
          Para abrir, seu perfil precisa da permissão de Configurações.
        </Typography>
      )}
    </Alert>
  );
};

export const WrapperDotz = ({ children, ...props }) => {
  const form = useFormContext();
  if (!form.getValues('useDotz')) return <></>;
  return (
    cloneElement(children, props)
  );
};

const RefreshAppInfoCacheButton: React.FC = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!record?.id) return null;

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await httpClientReactAdmin(
        `${DEFAULT_API_ADMIN}/chains/${record.id}/app-info/refresh-cache`,
        { method: 'POST' },
      );
      notify(
        `Cache do app regenerado.`,
        { type: 'success' },
      );
    } catch (err: any) {
      const message = err?.body?.message || err?.message || 'Erro ao atualizar o cache do app';
      notify(message, { type: 'error' });
    } finally {
      setLoading(false);
      setOpen(false);
    }
  };

  return (
    <>
      <MuiButton variant="text" size="small" startIcon={<CachedIcon fontSize="small" />} disabled={loading} onClick={() => setOpen(true)}>
        {loading ? 'Atualizando…' : 'Atualizar cache do app'}
      </MuiButton>
      <Confirm
        isOpen={open}
        loading={loading}
        title="Forçar atualização do cache do app"
        content="Limpa o cache (Redis) das configurações do app desta rede e gera um novo na hora, com os valores atuais do banco. Use se uma mudança de flag não refletiu no app. Continuar?"
        onConfirm={handleConfirm}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

const EditActions = () => (
  <TopToolbar>
    <ShowButton />
    <RefreshAppInfoCacheButton />
  </TopToolbar>
);

export default props => {

  const PostEditToolbar: React.FC<ToolbarProps> = props => {
    const [updating, setUpdating] = useState(false);
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();
    const redirect = useRedirect();
    const record = useRecordContext();
    const { resource } = useEditContext();

    const handleClick = () => {
      setUpdating(true);
      dataProvider.update(resource, {
        id: record.id,
        data: { isActive: !record?.isActive },
        previousData: { ...record }
      })
        .then(() => {
          notify(`Rede ${record?.isActive ? "desativada" : "ativada"} com sucesso`);
        }).catch(() => {
          notify(`Erro ao ${record?.isActive ? "desativar" : "ativar"} rede`, { type: 'warning' });
        }).finally(() => {
          setUpdating(false);
          refresh();
        });
    };

    return (
      <Toolbar {...props} style={{ display: "flex", justifyContent: "space-between" }}>
        <SaveButton />
        <Button
          startIcon={updating ? <LoadingIndicator /> : record?.isActive ? <RemoveCircleIcon /> : <AddCircleIcon />}
          label={record?.isActive ? "Desativar" : "Ativar"}
          onClick={handleClick}
          disabled={updating}
        />
      </Toolbar>
    );
  };

  const IdField = () => {
    const record = useRecordContext();
    if (!record) { return null; }

    const notify = useNotify();

    const handleClipboard = () => {
      if (record.id) {
        navigator.clipboard.writeText(record.id);
        notify("Id copiado para clipboard", { type: "info", autoHideDuration: 1000 });
      }
    }

    return (
      <SimpleShowLayout sx={containerIdFieldSx}>
        <TextField source="id" label="id" record={record} />
        <MuiButton onClick={handleClipboard} size="small" variant="outlined" sx={{ marginTop: "20px", marginLeft: "10px" }}>
          <ContentCopyIcon sx={{ fontSize: 18 }} />
        </MuiButton>
      </SimpleShowLayout>
    );
  }

  const RequireLocationInput = () => {
    const record = useRecordContext();

    return (
      <BooleanInput
        label="Conferir localização no abastecimento"
        source="requireLocationOnFillin"
        defaultValue={true}
        // Trava apenas quando já está salvo como true no banco (não deixa desabilitar
        // uma rede que já exige localização). Usar o valor "ao vivo" do formulário aqui
        // fazia o input se auto-desabilitar ao ligar, e o react-hook-form omite campos
        // disabled do submit — por isso não ia no PATCH e não salvava.
        disabled={record?.requireLocationOnFillin === true}
      />
    );
  };

  // TODO: Remove this transform when the following fields are fully implemented:
  // - metaAudienceNetworkAppId (Meta Audience Network App ID)
  // - metaAudienceNetworkDisplayName (Meta AN Display Name)
  // - applovinSdkKey (AppLovin SDK Key)
  // - pangleAppId (Pangle App ID)
  // These fields are defined in the model and admin UI but not yet in the database (createdb.sql)
  // and are not used in any backend service. Remove this filter once implementation is complete.
  const sanitizeChainData = (data) => {
    const {
      metaAudienceNetworkAppId,
      metaAudienceNetworkDisplayName,
      applovinSdkKey,
      pangleAppId,
      ...rest
    } = data;
    return rest;
  };

  return (
    <Edit {...props}
      resource="chains"
      title="Editar rede"
      transform={sanitizeChainData}
      actions={<EditActions />}
    >
      <TabbedForm toolbar={<PostEditToolbar {...props} />}>
        <FormTab label="Identificação">
          <IdField label="Id" />
          <TextInput source="name" label="Nome da rede/posto" InputProps={{ autoComplete: "do-not-autofill" }} sx={{ width: 350 }} fullWidth={false} />
          <PhoneInput
            InputProps={{ autoComplete: "do-not-autofill" }}
            source="phone"
            label="Telefone"
            sx={{ width: 200 }} fullWidth={false}
          />
          <TextInput source="email" label="Email" InputProps={{ autoComplete: "do-not-autofill" }} sx={{ width: 320 }} fullWidth={false} />
          <TextInput source="contact" label="Contato" InputProps={{ autoComplete: "do-not-autofill" }} sx={{ width: 250 }} fullWidth={false} />
          <TextInput source="emailNotice" label="Email para Avisos" validate={email()} inputProps={{ autoComplete: "do-not-autofill" }} sx={{ width: 320 }} fullWidth={false} />
          <ImageInput source="logo" label="Logo" accept={{ 'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'] }}>
            <ImageField source="src" title="Logo" />
          </ImageInput>
          <FileInput source="serviceAccount" label="Service Account" accept={{ 'application/json': ['.json'] }}>
            <FileField source="src" title="title" />
          </FileInput>
        </FormTab>
        {
          Permission({
            permission: PERMISSIONS.REGISTER_CHAIN_MODULE,
            children: (
              <FormTab label='Módulos'>
                <Grid container spacing={2} sx={{ maxWidth: 800 }} alignItems="center">
                  <Grid size={12}>
                    <Typography>
                      QrCode
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <PriceInput source="qrcodeValue" label="Valor" />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <BooleanInput source="useQrcode" label="Habilitar" />
                  </Grid>
                </Grid>
                <Divider />
                <Grid container spacing={2} sx={{ maxWidth: 800 }} alignItems="center">
                  <Grid size={12}>
                    <Typography>
                      Promoter
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <PriceInput source="promoterValue" label="Valor" />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <BooleanInput source="usePromoter" label="Habilitar" />
                  </Grid>
                </Grid>
                <Divider />
                <Grid container spacing={2} sx={{ maxWidth: 800 }} alignItems="center">
                  <Grid size={12}>
                    <Typography>
                      Anúncios
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <PriceInput source="advertisementValue" label="Valor" />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <BooleanInput source="useAdvertisement" label="Habilitar" />
                  </Grid>
                </Grid>
                <Divider />
                <Grid container spacing={2} sx={{ maxWidth: 800 }} alignItems="center">
                  <Grid size={12}>
                    <Typography>
                      Ads Pagos (AdMob) — Runtime
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Mudanças surtem efeito no próximo carregamento do app
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <BooleanInput source="hasPaidAds" label="Habilitar Ads Pagos" />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }} />
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextInput source="admobNativeUnitIdIos" label="AdMob Native Unit ID (iOS)" fullWidth />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextInput source="admobNativeUnitIdAndroid" label="AdMob Native Unit ID (Android)" fullWidth />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextInput source="admobMrecUnitIdIos" label="AdMob MREC Unit ID (iOS)" fullWidth />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextInput source="admobMrecUnitIdAndroid" label="AdMob MREC Unit ID (Android)" fullWidth />
                  </Grid>
                </Grid>
                <Divider />
                <Grid container spacing={2} sx={{ maxWidth: 800 }} alignItems="center">
                  <Grid size={12}>
                    <Typography>
                      Ads Pagos (AdMob) — Build-time
                    </Typography>
                    <Typography variant="caption" color="warning.main">
                      Mudanças exigem rebuild do app via wl-script
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextInput source="admobAppIdIos" label="AdMob App ID (iOS)" fullWidth />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextInput source="admobAppIdAndroid" label="AdMob App ID (Android)" fullWidth />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextInput source="metaAudienceNetworkAppId" label="Meta Audience Network App ID" fullWidth />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextInput source="metaAudienceNetworkDisplayName" label="Meta AN Display Name" fullWidth />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextInput source="applovinSdkKey" label="AppLovin SDK Key" fullWidth />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextInput source="pangleAppId" label="Pangle App ID" fullWidth />
                  </Grid>
                </Grid>
                <Divider />
                <Grid container spacing={2} sx={{ maxWidth: 800 }} alignItems="center">
                  <Grid size={12}>
                    <Typography>
                      Sorteios
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <PriceInput source="drawingValue" label="Valor" />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <BooleanInput source="useDrawing" label="Habilitar" />
                  </Grid>
                </Grid>
                <Divider />
                <Grid container spacing={2} sx={{ maxWidth: 800 }} alignItems="center">
                  <Grid size={12}>
                    <Typography>
                      Clube de Vantagens
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <PriceInput source="giftValue" label="Valor" />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <BooleanInput source="useGift" label="Habilitar" />
                  </Grid>
                </Grid>
                <Divider />
                <Grid container spacing={2} sx={{ maxWidth: 800 }} alignItems="center">
                  <Grid size={12}>
                    <Typography>
                      Bolão da Copa
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 12 }}>
                    <BooleanInput source="useBolao" label="Habilitar bolão (mobile mostra cards e admin libera abas)" />
                  </Grid>
                </Grid>
                <Divider />
                <Grid container spacing={2} sx={{ maxWidth: 800 }} alignItems="center">
                  <Grid size={12}>
                    <Typography>
                      Roleta da Sorte
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 12 }}>
                    <BooleanInput source="useRoleta" label="Habilitar Roleta da Sorte (app mostra a roleta e o admin libera o cadastro)" />
                  </Grid>
                </Grid>
                <Divider />
                <Grid container spacing={2} sx={{ maxWidth: 800 }} alignItems="center">
                  <Grid size={12}>
                    <Typography>
                      Recarga Elétrica (InCharge)
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 12 }}>
                    <BooleanInput
                      source="useIncharge"
                      label="Habilitar leitura do QR do carregador elétrico no app"
                      helperText="Depende de acordo com a InCharge e das estações cadastradas para os postos da rede."
                    />
                  </Grid>
                </Grid>
                <Divider />
                <Grid container spacing={2} sx={{ maxWidth: 800 }} alignItems="center">
                  <Grid size={12}>
                    <Typography>
                      Dotz
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <PriceInput source="dotzValue" label="Valor" />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextInput
                      source="dotzFactor"
                      label="Fator de conversão"
                      defaultValue={0}
                      format={value => (!value ? '0,000' : `${Number(value).toFixed(3)}`)}
                      parse={value => (!value ? 0 : +value.replace(/[^\d]/g, '') / 1000)}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <BooleanInput source="useDotz" label="Habilitar" />
                  </Grid>
                </Grid>
                <WrapperDotz>
                  <Grid container spacing={2} sx={{ maxWidth: 800 }} alignItems="center">
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextInput source="dotzUser" label="Usuário" fullWidth />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <PasswordInput source="dotzPassword" label="Senha" inputProps={{ autocomplete: 'current-password' }} fullWidth />
                    </Grid>
                  </Grid>
                </WrapperDotz>
                <Divider />
                <Grid container spacing={2} sx={{ maxWidth: 800 }} alignItems="center">
                  <Grid size={12}>
                    <Typography>
                      Meta de frentista
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <PriceInput source="attendantTargetValue" label="Valor" />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <BooleanInput source="useAttendantTarget" label="Habilitar" />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <DateInput source="attendantTargetExpiresAt" label="Degustação até" helperText="Deixar vazio para acesso permanente" fullWidth />
                  </Grid>
                </Grid>
                <Divider />
                <Grid container spacing={2} sx={{ maxWidth: 800 }} alignItems="center">
                  <Grid size={12}>
                    <Typography>
                      Limites de Pontuação
                    </Typography>
                  </Grid>
                   <Grid size={{ xs: 12, md: 6 }}>
                    <PriceInput source="limitsModuleValue" label="Valor" />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <BooleanInput label="Habilitar" source="useLimits" />
                  </Grid>
                </Grid>
                <Divider />
                <Grid container spacing={2} sx={{ maxWidth: 800 }} alignItems="center">
                  <Grid size={12}>
                    <Typography>
                      Análise de Comentários
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <BooleanInput label="Habilitar" source="useCommentAnalysis" />
                  </Grid>
                </Grid>
                <Divider />
                <Grid container spacing={2} sx={{ maxWidth: 800 }} alignItems="center">
                  <Grid size={12}>
                    <Typography>
                      Agente de WhatsApp
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Libera a tela "Números de WhatsApp" para esta rede. Só depois
                      de habilitar aqui é que a rede consegue cadastrar números.
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <PriceInput source="whatsappAgentValue" label="Valor" />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <BooleanInput label="Habilitar" source="useWhatsappAgent" />
                  </Grid>
                </Grid>
              </FormTab>
            )
          })
        }
        <FormTab label="Integrações">
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Vendas Externas</Typography>
            <Tooltip sxContainer={{ alignItems: 'flex-start' }} placement="top-start" title="Quando desativado, o endpoint de vendas externas não aceitará novas vendas para esta rede.">
              <BooleanInput label="Aceitar vendas externas" source="acceptExternalSales" />
            </Tooltip>
            <Divider />
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>SGA</Typography>
            <BooleanInput label="Habilitar SGA" source="useSgaIntegration" />
            <Divider />
            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>Banco do Brasil PIX</Typography>
            <TextInput source="bbClientId" label="Client ID" fullWidth />
            <PasswordInput source="bbClientSecret" label="Client Secret" fullWidth />
            <TextInput source="bbDeveloperAppKey" label="Developer App Key" fullWidth />
            <FileInput source="bbCertificate" label="Certificado A1 (.pfx)" accept={{ 'application/x-pkcs12': ['.pfx', '.p12'] }}>
              <FileField source="src" title="title" />
            </FileInput>
            <PasswordInput source="bbCertificatePassword" label="Senha do Certificado" fullWidth />
            <Divider />
            <Grid container spacing={2} sx={{ maxWidth: 800 }} alignItems="flex-start">
              <Grid size={12}>
                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                  Clube Certo
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <CNPJInput source="clubeCertoLogin" label="CNPJ" fullWidth />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <PasswordInput source="clubeCertoPassword" label="Senha" inputProps={{ autocomplete: 'new-password' }} fullWidth />
              </Grid>
            </Grid>
        </FormTab>
        <FormTab label="Revenda">
          <Grid container spacing={2} sx={{ maxWidth: 800 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <ReferenceArrayInput
                reference="retailers"
                source="retailerIds"
                sort={{ field: 'name', order: 'ASC' }}
                //@ts-ignore
                perPage={null}
                style={{ minWidth: 220, marginBottom: -16 }}
              >
                <SelectArrayInput label="Revendedores" optionText="name" fullWidth />
              </ReferenceArrayInput>
            </Grid>
          </Grid>
        </FormTab>
        {/* Aba antiga mantida só com o aviso: é aqui que o operador procura essas opções. */}
        <FormTab label="Aplicativo">
          <MovedToAppConfigNotice />
        </FormTab>
        <FormTab label="Histórico">
          <EntityChangeLogTab entityType="chain" />
        </FormTab>
        <FormTab label="Configurações">
          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>Abastecimento</Typography>
          <Tooltip sxContainer={{ alignItems: 'flex-start' }} placement="top-start" title="Quando marcado, o encerrante deverá ser informado obrigatoriamente em formulários como o de adição de pontos.">
            <BooleanInput label="Obrigar a informar encerrante" source="mustInformEncerrante" />
          </Tooltip>
          <Tooltip sxContainer={{ alignItems: 'flex-start' }} placement="top-start" title="Quando marcado, o bico deverá ser informado obrigatoriamente em formulários como o de adição de pontos.">
            <BooleanInput label="Obrigar a informar bico" source="mustInformHoseNumber" />
          </Tooltip>
          <Tooltip sxContainer={{ alignItems: 'flex-start' }} placement="top-start" title="Quando ativado, o aplicativo obrigará o envio de localização e a proximidade do cliente ao posto para fazer o abastecimento lendo o QrCode.">
            <RequireLocationInput />
          </Tooltip>
          <Tooltip sxContainer={{ alignItems: 'flex-start' }} placement="top-start" title="Quando desativado, possibilitará que o cliente abasteça sem estar no posto por 15 minutos. Após esse prazo, essa opção ficará ativa novamente de forma automática.">
            <BooleanInput label="Conferir localização no abastecimento (desativação temporária de 15 minutos)" source="locationConfigEnabled" />
          </Tooltip>
          <BooleanInput label="Usa código no frentista" source="useAttendantCode" />
          <Tooltip sxContainer={{ alignItems: 'flex-start' }} placement="top-start" title="Tempo máximo, em minutos, para buscar os abastecimentos recentes do bico na tela de Adicionar Pontos por Abastecimento. Em branco, usa o padrão de 30 minutos.">
            <NumberInput source="lastFillinsWindowMinutes" label="Tempo máximo do abastecimento (minutos)" validate={[minValue(1), maxValue(1440)]} helperText="Padrão: 30 minutos" sx={{ width: 320 }} />
          </Tooltip>
          <Divider />
          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>Preços e Descontos</Typography>
          <BooleanInput label="Habilitar acréscimo" source="isPriceIncreaseEnabled" />
          <BooleanInput label="Habilitar descontos múltiplos" source="isMultipleDiscountsEnabled" />
          <Divider />
          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>Dotz</Typography>
          <Tooltip sxContainer={{ alignItems: 'flex-start' }} placement="top-start" title="Quando ativado, as vendas (abastecimentos) serão enviadas automaticamente para a Dotz, gerando Dotz ao invés de pontos na Pontuax.">
            <BooleanInput label="Enviar vendas para Dotz" source="sendSalesToDotz" />
          </Tooltip>
          <Divider />
          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>Regras de Pontuação</Typography>
          <Tooltip sxContainer={{ alignItems: 'flex-start' }} placement="top-start" title="Quando ativado, habilita o uso das novas regras de pontuação (scorings) e exibe o menu de criação no admin.">
            <BooleanInput label="Usar novas regras de pontuação" source="useScoringRules" />
          </Tooltip>
          <MigrateGroupsToTagsDialog />
          <Divider />
          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>Recuperação de Clientes</Typography>
          <BooleanInput label="Habilitar recuperação de clientes" source="useCustomerRecovery" />
        </FormTab>
      </TabbedForm>
    </Edit>
  );
}